import { Link } from "react-router-dom";
import { ArrowRight, Shield } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CtaButton } from "@/components/cta/CtaButton";
import SectionAtmosphere from "@/components/SectionAtmosphere";
import atmosphereFinal from "@/assets/atmosphere-finalcta.jpg";
import { lqipFor } from "@/generated/atmospheric-lqip";
import { riseFade } from "@/lib/motion";
import { prefetchOn } from "@/lib/prefetchRoute";
import { trackCtaClick } from "@/lib/observability";
import { openRequestAccess } from "./RequestAccessDialog";

const FinalCTA = () => (
  <SectionAtmosphere
    ariaLabelledBy="final-cta-heading"
    src={atmosphereFinal}
    optimizedBase="/opt/atmosphere-finalcta"
    lqip={lqipFor(atmosphereFinal)}
    origin="center"
    intensity={0.14}
    glow="center"
    tone="cream"
    className="py-32 sm:py-40"
  >
    <div
      data-test="final-cta"
      className="container mx-auto px-6 sm:px-10 lg:px-16"
    >
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-10% 0px" }}
        variants={riseFade}
        className="max-w-3xl mx-auto text-center space-y-8"
      >
        <div className="eyebrow justify-center mx-auto">
          <Shield className="w-3 h-3" aria-hidden="true" />
          Wellness and informational support
        </div>

        <h2 id="final-cta-heading" className="font-display text-foreground text-display-2xl">
          Review what you logged. <span className="italic text-primary">Plan with more context.</span>
        </h2>

        <p className="lead-italic max-w-xl mx-auto">
          A brief check-in and retrospective view can help you remember what happened over time. Myelina does not predict tomorrow, diagnose a cause, recommend treatment, or provide emergency support.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <CtaButton
            intent="requestAccess"
            size="lg"
            source="final_cta"
            analyticsMetadata={{ signup: true, destination: "request-access" }}
            onClick={() => openRequestAccess("final_cta")}
            className="w-full sm:w-auto px-8 min-h-11"
          />
          <Button
            asChild
            variant="outline"
            size="lg"
            className="group w-full sm:w-auto px-8 min-h-11"
          >
            <Link
              to="/demo"
              {...prefetchOn("/demo")}
              onClick={() => trackCtaClick("final_cta", "try_the_demo", { destination: "/demo" })}
              aria-label="Try the demo, opens the interactive check-in"
            >
              Try the demo
              <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Button>
        </div>

        <p className="text-[11px] text-muted-foreground pt-1">
          Review our{" "}
          <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>
          {" and "}
          <Link to="/security" className="underline hover:text-foreground">data-use information</Link>
          {" before deciding whether to request access. Available choices depend on the current product version."}
        </p>
      </motion.div>
    </div>
  </SectionAtmosphere>
);

export default FinalCTA;
